import * as EmojiAware from 'emoji-aware'
import {
  component,
  computed,
  observable,
  observableArray,
  subscribe,
} from 'knockout-decorators'
import { compareTwoStrings } from 'string-similarity'

import source from './page-header.html?raw'
import './page-header.less'

import {
  CollectorLink,
  EventItem,
  Location,
  Organization,
  SearchResults,
} from '@tixa/schema'
import { Analytics, Authentication, ErrorReporter } from '~/utils'
import { pageState } from '~/store/page'

import CreditCardImages from '../../images/cards.svg?raw'
import SearchIcon from '../../images/icons/search.svg?raw'
import FacebookLogo from '../../images/logo-facebook.svg?raw'
import InstagramLogo from '../../images/logo-instagram.svg?raw'
import TixaBlogLogo from '../../images/logo-tixablog.svg?raw'
import LogoImage from '../../images/tixa-logo.svg?raw'
import UserIcon from '../../images/user-icon-2.svg?raw'
import { Locale } from '~/utils/language'
import { AppNavigation } from '~/utils/navigation'

const SimpleWhiteLogo = new URL(
  '../../images/simplepay_w240-white.png',
  import.meta.url
)

const KNHLogo = new URL('../../images/k&h_logo.png', import.meta.url).href

const SIMPLEPAY_INFO_URL_HU =
  'https://simplepartner.hu/PaymentService/Fizetesi_tajekoztato.pdf'
const SIMPLEPAY_INFO_URL_OTHER =
  'https://simplepartner.hu/PaymentService/Payment_information.pdf'

@component('page-header', source)
export class PageHeader {
  @computed get overallResults(): number {
    return (
      this.eventResults.length +
      this.locationResults.length +
      this.organizerResults.length +
      this.collectorResults.length
    )
  }

  @computed get showSearch(): boolean {
    return this.searchTerm.length >= this.searchMinCharCount
  }

  @computed get showNoresults(): boolean {
    return (
      (this.searchTerm.length >= this.searchMinCharCount ||
        this.overallResults === 0) &&
      this.searchRunning === false
    )
  }

  @computed get showClose(): boolean {
    return (
      this.showSearch && false === this.searchRunning && this.overallResults > 0
    )
  }

  @computed get locationResultsSlice(): Location[] {
    return this.locationResults.slice(0, this.searchDropdownMaxPerCategory)
  }

  @computed get organizerResultsSlice(): Organization[] {
    return this.organizerResults.slice(0, this.searchDropdownMaxPerCategory)
  }

  @computed get eventResultsSlice(): EventItem[] {
    return this.eventResults.slice(0, this.searchDropdownMaxPerCategory)
  }

  @computed get collectorResultsSlice(): CollectorLink[] {
    return this.collectorResults.slice(0, this.searchDropdownMaxPerCategory)
  }

  @computed get hasMoreLocations(): boolean {
    return this.locationTotal > this.locationResults.length
  }

  @computed get hasMoreOrganizers(): boolean {
    return this.organizerTotal > this.organizerResults.length
  }

  @computed get hasMoreEvents(): boolean {
    return this.eventTotal > this.eventResults.length
  }

  @computed get hasMoreCollectors(): boolean {
    return this.collectorTotal > this.collectorResults.length
  }

  @computed get moreLocationsUrl(): string {
    return this.getSearchResultsUrl('location')
  }

  @computed get moreOrganizersUrl(): string {
    return this.getSearchResultsUrl('organizer')
  }

  @computed get moreEventsUrl(): string {
    return this.getSearchResultsUrl('event')
  }

  @computed get moreCollectorsUrl(): string {
    return this.getSearchResultsUrl('collector')
  }

  @computed
  get isLoginActive(): boolean {
    return this.navState === 'login'
  }

  @computed
  get isLanguageActive(): boolean {
    return this.navState === 'language'
  }

  @observable public searchFocus = false
  @observable public searchRunning = false
  @observable public searchTerm = ''
  @observableArray public eventResults: EventItem[] = []
  @observableArray public locationResults: Location[] = []
  @observableArray public organizerResults: Organization[] = []
  @observableArray public collectorResults: CollectorLink[] = []
  @observable public eventTotal = 0
  @observable public locationTotal = 0
  @observable public organizerTotal = 0
  @observable public collectorTotal = 0

  @observable public isLoggedIn = false

  // Mobile nav

  @observable public isHomepage: boolean = window.location.pathname.length <= 1
  @observable public navState = ''
  @observable public showCollapse = false
  @observable public isSearchActive = false

  private Tracker = Analytics.getInstance()
  private Auth = Authentication.getInstance()

  private readonly searchMinCharCount = 3
  private readonly searchMaxResults = 10
  private readonly searchDropdownMaxPerCategory = 2

  private lastCancelToken = new AbortController()
  private currentSearchTerm = ''
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
  private readonly searchDebounceMs = 350

  private logoImage = LogoImage
  private searchIcon = SearchIcon
  private userIcon = UserIcon
  private creditCardImages = CreditCardImages
  private facebookLogo = FacebookLogo
  private instagramLogo = InstagramLogo
  private blogLogo = TixaBlogLogo
  private simpleLogo = SimpleWhiteLogo
  private knhLogo = KNHLogo

  @computed get simplePayInfoUrl(): string {
    return Locale.language === 'hu'
      ? SIMPLEPAY_INFO_URL_HU
      : SIMPLEPAY_INFO_URL_OTHER
  }

  constructor() {
    const searchEl = window.document.getElementById(
      'search'
    ) as HTMLInputElement
    if (searchEl) {
      searchEl.onfocus = () => {
        this.searchFocus = true
      }
      searchEl.onblur = () => {
        this.searchFocus = false
        setTimeout(() => {
          ;(
            document.querySelector('img.background') as HTMLImageElement
          ).style.transform = `scale(1)`
        }, 200)
      }
      searchEl.onkeydown = (e) => {
        if (e.key === 'Enter') {
          this.goToSearchResults()
        }
      }
    }
    subscribe<string>(
      () => this.searchTerm,
      (newTerm) => {
        if (this.searchDebounceTimer != null) {
          clearTimeout(this.searchDebounceTimer)
          this.searchDebounceTimer = null
        }
        if (newTerm.length < this.searchMinCharCount) {
          this.clearResults()
          return
        }
        this.searchDebounceTimer = setTimeout(() => {
          this.searchDebounceTimer = null
          this.search(newTerm)
        }, this.searchDebounceMs)
      }
    )
    this.isLoggedIn = this.Auth.LoggedIn()
  }

  private search(term: string) {
    this.cancelLastSearch()
    this.currentSearchTerm = term
    if (term.length >= this.searchMinCharCount) {
      this.clearResults()
      this.searchRunning = true
      const searchTerm = EmojiAware.withoutEmoji(term).join('')

      pageState.api.search
        .search(
          searchTerm,
          this.lastCancelToken,
          this.searchDropdownMaxPerCategory
        )
        .then((value) => {
          if (term !== this.currentSearchTerm) return
          this.processSearchResults(term, value)
          this.searchRunning = false
        })
        .catch((error) => {
          if (error.name !== 'CanceledError') {
            ErrorReporter.exception(error)
          }
          if (term === this.currentSearchTerm) {
            this.clearResults()
            this.searchRunning = false
          }
        })

      if (this.Tracker && this.Tracker.searched) {
        this.Tracker.searched(term)
      }
    } else {
      this.clearResults()
    }
  }

  private processSearchResults(term: string, results: SearchResults) {
    if (!results) {
      return
    }
    const locations = results.locations?.items ?? []
    const organizers = results.organizers?.items ?? []
    const events = results.events?.items ?? []
    const collectors = results.collectors?.items ?? []

    this.locationTotal = results.locations?.total ?? 0
    this.organizerTotal = results.organizers?.total ?? 0
    this.eventTotal = results.events?.total ?? 0
    this.collectorTotal = results.collectors?.total ?? 0

    this.locationResults = this.similaritySorter<Location>(
      term,
      locations,
      (location) => location.name
    )
    this.organizerResults = this.similaritySorter<Organization>(
      term,
      organizers,
      (organization) => organization.name
    )
    this.collectorResults = this.similaritySorter<CollectorLink>(
      term,
      collectors,
      (link) => link.name
    )

    const resultsSoFar =
      this.locationResults.length +
      this.organizerResults.length +
      this.collectorResults.length
    this.eventResults = this.similaritySorter<EventItem>(
      term,
      events,
      (event) => event.name
    ).slice(0, this.searchMaxResults - resultsSoFar)
  }

  private similaritySorter<T>(
    term: string,
    array: T[],
    key: (obj: T) => string
  ): T[] {
    const keySimilarity = array.map((item) =>
      compareTwoStrings(term, key(item))
    )
    return array.sort((a, b) =>
      this.sortComparator<T>(a, b, (obj) => keySimilarity[array.indexOf(obj)])
    )
  }

  private sortComparator<T>(a: T, b: T, key: (obj: T) => number): number {
    return key(a) > key(b) ? -1 : key(a) > key(b) ? 1 : 0
  }

  private clearResults() {
    this.eventResults = []
    this.locationResults = []
    this.organizerResults = []
    this.collectorResults = []
    this.eventTotal = 0
    this.locationTotal = 0
    this.organizerTotal = 0
    this.collectorTotal = 0
  }

  private cancelLastSearch() {
    if (this.lastCancelToken) {
      this.lastCancelToken.abort()
      this.searchRunning = false
      this.lastCancelToken = new AbortController()
    }
  }

  private formatDate(event: EventItem) {
    // return fecha.format(new Date(date), "MMMM DD");
    return event.customDate ? event.customDate : event.startDate
  }

  private async ticketsClicked() {
    if (this.Auth.LoggedIn()) {
      this.redirectToTickets()
    } else {
      this.Auth.Login(() => this.redirectToTickets())
    }
  }

  private async toggleCollapse() {
    this.showCollapse = !this.showCollapse
    this.isSearchActive = false
    await this.Auth.HideDiag()
    pageState.blockAnimations = this.showCollapse
  }

  private async profileClicked() {
    await this.Auth.ShowProfileDiag()
  }

  private redirectToTickets() {
    AppNavigation.tickets()
  }

  private async otherLangClicked() {
    await AppNavigation.switchLanguage()
  }

  private closeSearch() {
    if (this.lastCancelToken) {
      this.lastCancelToken.abort()
    }
    this.clearResults()
    this.searchTerm = ''
  }

  private goToSearchResults() {
    window.location.href = this.getSearchResultsUrl()
  }

  getSearchResultsUrl(tab?: string): string {
    const term = this.searchTerm.trim()
    const params = new URLSearchParams()
    if (term) params.set('q', term)
    if (tab) params.set('tab', tab)
    const query = params.toString()
    const configPath = pageState.config?.searchResultsPath
    const path =
      configPath != null && configPath !== ''
        ? configPath.startsWith('/')
          ? configPath
          : `/${configPath}`
        : (() => {
            const base =
              (typeof import.meta !== 'undefined' &&
                import.meta.env?.BASE_URL) ||
              '/'
            return base.endsWith('/')
              ? `${base}search-results.html`
              : `${base}/search-results.html`
          })()
    return `${path}${query ? `?${query}` : ''}`
  }

  private homeClicked() {
    const base =
      (typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL) || '/'
    window.location.href = base
  }

  private async searchClicked() {
    this.isSearchActive = !this.isSearchActive
    this.showCollapse = false
    await this.Auth.HideDiag()
    pageState.blockAnimations = this.isSearchActive
  }

  private languageClicked() {
    this.navState = this.navState === 'language' ? '' : 'language'
  }
}
